import { useEffect, useState } from "react";
import type { ComponentInfo, ComponentParam } from "../lib/api.ts";
import type { EditorStore } from "../lib/store.ts";
import { addComponentInstance, sceneAt, sceneOf } from "../lib/ops.ts";
import { t } from "../lib/i18n.ts";
import { ApiError } from "../lib/api.ts";
import { Err, Spinner, msgOf, useApp } from "./common.tsx";

const defaults = (c: ComponentInfo): Record<string, unknown> => Object.fromEntries(c.params.map((p) => [p.name, p.default]));
const slugify = (s: string): string => (s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "composant").slice(0, 36);

function ParamField({ p, value, onChange }: { p: ComponentParam; value: unknown; onChange: (v: unknown) => void }) {
  const label = p.label || p.name;
  if (p.type === "number") return <label className="field"><span>{label}</span><input className="input" type="number" min={p.min ?? undefined} max={p.max ?? undefined} value={String(value)}
    onChange={(e: any) => { const v = parseFloat(e.target.value); if (Number.isFinite(v)) onChange(v); }} /></label>;
  if (p.type === "color") return <label className="field"><span>{label}</span><input type="color" value={String(value)} onChange={(e: any) => onChange(e.target.value.toUpperCase())} /></label>;
  if (p.type === "bool") return <label className="field"><span>{label}</span><input type="checkbox" checked={Boolean(value)} onChange={(e: any) => onChange(e.target.checked)} /></label>;
  if (p.type === "choice") return <label className="field"><span>{label}</span><select className="input" value={String(value)} onChange={(e: any) => onChange(e.target.value)}>
    {(p.options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}</select></label>;
  return <label className="field"><span>{label}</span><input className="input" maxLength={280} value={String(value)} onChange={(e: any) => onChange(e.target.value)} /></label>;
}

/** Library of declarative components. The server expands a component into ordinary elements and keyframes; they are then
 *  added through the editor store, so adding one is a normal undoable edit that autosaves like any other. */
export function ComponentsPanel({ store, projectId }: { store: EditorStore; projectId: string }) {
  const { api } = useApp();
  const [list, setList] = useState(null as ComponentInfo[] | null);
  const [open, setOpen] = useState(null as string | null);
  const [vals, setVals] = useState({} as Record<string, unknown>);
  const [err, setErr] = useState(null as string | null);
  const [note, setNote] = useState(null as string | null);
  const [busy, setBusy] = useState(false);
  const [aiText, setAiText] = useState("");
  const [prop, setProp] = useState(null as { action: string; slug: string; definition: Record<string, unknown>; name: string } | null);
  async function propose() {
    if (!aiText.trim()) return;
    setBusy(true); setErr(null); setNote(null); setProp(null);
    try {
      const r = await api.generateComponent(aiText.trim());
      const name = String((r.definition as any).name ?? r.slug);
      await refresh();
      if (r.action === "reuse") { setNote(t("cmp.ai_reuse", { name })); setOpen(r.slug); setVals({ ...defaults((list ?? []).find((c) => c.slug === r.slug) ?? ({ params: [] } as any)), ...r.params }); }
      else setProp({ action: r.action, slug: r.slug, definition: r.definition, name });
    } catch (e) { setErr(msgOf(e)); } finally { setBusy(false); }
  }
  async function keep() {
    if (!prop) return;
    setBusy(true); setErr(null);
    try { await api.createComponent(prop.definition); setProp(null); setAiText(""); await refresh(); setOpen(prop.slug); setNote(t("cmp.saved")); } catch (e) { setErr(msgOf(e)); } finally { setBusy(false); }
  }
  const refresh = () => api.listComponents().then((r) => setList(r.components), (e) => setErr(msgOf(e)));
  useEffect(() => { void refresh(); }, []);

  const choose = (c: ComponentInfo) => { if (open === c.slug) { setOpen(null); return; } setOpen(c.slug); setVals(defaults(c)); setErr(null); setNote(null); };

  async function add(c: ComponentInfo) {
    const s = store.getState(), p = s.project; if (!p) return;
    const sel = s.selection;
    const sid = sel.kind === "scene" ? sel.id : sel.kind === "element" ? sceneOf(p, sel.id)?.id : (sceneAt(p, s.timeMs)?.id ?? p.scenes[p.scenes.length - 1]?.id);
    if (!sid) { setErr(t("lib.no_scene")); return; }
    let n = 1; while (Object.keys(p.elements).some((id) => id.startsWith(`c${n}__`))) n++;
    setBusy(true); setErr(null); setNote(null);
    try {
      const ex = await api.previewComponent(c.slug, { params: vals, prefix: `c${n}`, start_ms: 0 });
      const r = addComponentInstance(store.getState().project!, sid, ex, p.width / 2, p.height / 2);
      if ("error" in r) {
        setErr(r.error === "too_long" ? t("cmp.too_long", { need: r.needed_ms ?? 0, scene: p.scenes.find((x) => x.id === sid)!.duration_ms }) : r.error === "too_many" ? t("cmp.too_many") : t("err.generic"));
        return;
      }
      store.edit(() => r.project); store.select({ kind: "element", id: r.ids[r.ids.length - 1]! });
      setNote(t("cmp.added", { name: c.name }));
    } catch (e) { setErr(msgOf(e)); } finally { setBusy(false); }
  }

  async function saveSelection() {
    const s = store.getState();
    if (s.selection.kind !== "element") { setErr(t("cmp.select_first")); return; }
    const name = (prompt(t("cmp.name_prompt")) ?? "").trim(); if (!name) return;
    setBusy(true); setErr(null); setNote(null);
    try {
      await store.saveNow();                                  // the server builds the component from the saved project
      await api.saveSelectionAsComponent(projectId, { element_ids: [s.selection.id], slug: `${slugify(name)}-${Math.random().toString(36).slice(2, 6)}`, name });
      await refresh(); setNote(t("cmp.saved"));
    } catch (e) { setErr(msgOf(e)); } finally { setBusy(false); }
  }

  async function remove(c: ComponentInfo) {
    setErr(null);
    try { await api.deleteComponent(c.slug); setOpen(null); await refresh(); } catch (e) { setErr(e instanceof ApiError ? msgOf(e) : t("err.generic")); }
  }

  const row = (c: ComponentInfo) => (
    <div key={c.slug} className="cmp-row">
      <button className="layer" aria-expanded={open === c.slug} title={c.description} onClick={() => choose(c)}><strong>{c.name}</strong> <small>{c.category}</small></button>
      {open === c.slug && <div className="cmp-form">
        <p className="muted" style={{ fontSize: 12, margin: "4px 0" }}>{c.description}{c.uses.length ? ` · ${t("cmp.uses", { n: c.uses.length })}` : ""}</p>
        {c.params.map((p) => <ParamField key={p.name} p={p} value={vals[p.name]} onChange={(v) => setVals({ ...vals, [p.name]: v })} />)}
        <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
          <button className="btn sm primary" disabled={busy} onClick={() => void add(c)}>{busy ? <Spinner /> : null} {t("cmp.add")}</button>
          {!c.builtin && <button className="btn sm ghost" onClick={() => void remove(c)}>{t("cmp.delete")}</button>}
        </div>
      </div>}
    </div>
  );
  const mine = (list ?? []).filter((c) => !c.builtin), ready = (list ?? []).filter((c) => c.builtin);
  return (
    <div className="cmp-panel">
      <h3 className="h">{t("cmp.title")}</h3>
      <button className="btn sm" style={{ width: "100%", marginBottom: 6 }} disabled={busy} onClick={() => void saveSelection()}>{t("cmp.save_sel")}</button>
      <label className="field"><span>{t("cmp.ai")}</span><textarea className="input" rows={2} maxLength={1500} placeholder={t("cmp.ai_ph")} value={aiText} onChange={(e: any) => setAiText(e.target.value)} /></label>
      <button className="btn sm" style={{ width: "100%", marginBottom: 6 }} disabled={busy || !aiText.trim()} onClick={() => void propose()}>{busy ? <Spinner /> : null} {t("cmp.ai_go")}</button>
      {prop && <div className="alert" role="status"><p style={{ margin: "0 0 6px" }}>{t("cmp.ai_new", { name: prop.name })}</p><button className="btn sm primary" disabled={busy} onClick={() => void keep()}>{t("cmp.ai_keep")}</button></div>}
      {err && <Err>{err}</Err>}
      {note && <div className="alert" role="status">{note}</div>}
      {!list && <Spinner />}
      {mine.length > 0 && <><h4 className="h2">{t("cmp.mine")}</h4>{mine.map(row)}</>}
      {ready.length > 0 && <><h4 className="h2">{t("cmp.ready")}</h4>{ready.map(row)}</>}
    </div>
  );
}
